// Vuex draft-persistence plugin factory.
//
// The congress + letters letter-composer stores both persist an in-progress
// draft to sessionStorage and restore it on load. The logic is identical; only
// the storage key, the persisted field list, and the mutations to skip differ.
// This factory captures that one implementation.
//
// Usage:
//   const plugin = createDraftPersistencePlugin({
//     storageKey: 'congress-draft',
//     fields: ['currentStep', 'senderAddress', ...],
//     skipMutations: ['SET_USER', 'SET_AUTH_LOADING', ...],
//   });
//   createStore({ ..., plugins: [plugin] });
// and in the clearDraft action: clearDraftStorage('congress-draft').
export function createDraftPersistencePlugin({ storageKey, fields, skipMutations = [] }) {
    function loadDraft() {
        try {
            const saved = sessionStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            console.warn('Failed to load draft from session storage:', e);
            return null;
        }
    }

    function saveDraft(state) {
        try {
            const draft = {};
            fields.forEach((field) => {
                draft[field] = state[field];
            });
            sessionStorage.setItem(storageKey, JSON.stringify(draft));
        } catch (e) {
            console.warn('Failed to save draft to session storage:', e);
        }
    }

    return (store) => {
        // Merge any saved draft into the initial state on store creation.
        const savedDraft = loadDraft();
        if (savedDraft) {
            Object.keys(savedDraft).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(store.state, key)) {
                    store.state[key] = savedDraft[key];
                }
            });
        }

        // Persist on every mutation except the skipped (auth/loading) ones.
        store.subscribe((mutation, state) => {
            if (!skipMutations.includes(mutation.type)) {
                saveDraft(state);
            }
        });
    };
}

// Clear a persisted draft (call when the letter is sent or the user starts over).
export function clearDraftStorage(storageKey) {
    sessionStorage.removeItem(storageKey);
}
